import type { StepConfig } from "./stepConfig";

export const API_FRAGMENT_FILE = "api.json";

export function apiFragmentPath(stepDir: string): string {
  return `${stepDir}/${API_FRAGMENT_FILE}`;
}

const HTTP_METHODS = new Set([
  "get",
  "post",
  "put",
  "delete",
  "patch",
  "head",
  "options",
  "trace",
]);

// Paths, auth, and servers are derived from config.toml at serve time, so the
// fragment must not restate them — allowing that invites contradiction.
const DERIVED_OPERATION_FIELDS = ["security", "servers"] as const;

// Fragments have no components section, so such a ref dangles both in the
// assembled spec and when a step validates inputs against its own schemas.
function findComponentsRef(node: unknown): boolean {
  if (typeof node !== "object" || node === null) return false;
  if (Array.isArray(node)) return node.some(findComponentsRef);
  for (const [key, value] of Object.entries(node)) {
    if (
      key === "$ref" &&
      typeof value === "string" &&
      value.startsWith("#/components/")
    )
      return true;
    if (findComponentsRef(value)) return true;
  }
  return false;
}

/**
 * Validates a step's api.json fragment against its config. Returns a
 * user-facing error message, or null when valid.
 */
export function validateApiFragment(
  stepDir: string,
  raw: string,
  config: Pick<StepConfig, "route" | "routeType"> | null
): string | null {
  const file = apiFragmentPath(stepDir);

  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return `${file} is not valid JSON.`;
  }
  if (typeof doc !== "object" || doc === null || Array.isArray(doc)) {
    return `${file} must be a JSON object keyed by lowercase HTTP method.`;
  }

  const entries = Object.entries(doc);
  if (entries.length === 0) {
    return `${file} must document at least one HTTP method.`;
  }
  for (const [method, operation] of entries) {
    if (!HTTP_METHODS.has(method)) {
      return `${file} keys must be lowercase HTTP methods, got "${method}".`;
    }
    if (
      typeof operation !== "object" ||
      operation === null ||
      Array.isArray(operation)
    ) {
      return `${file}: "${method}" must be an OpenAPI operation object.`;
    }
    for (const field of DERIVED_OPERATION_FIELDS) {
      if (field in operation) {
        return `${file}: "${method}" must not set "${field}" — it is derived from config.toml.`;
      }
    }
    if (findComponentsRef(operation)) {
      return `${file}: "${method}" must be self-contained — "$ref" to "#/components/…" is not allowed; inline the schema instead.`;
    }
  }

  if (!config?.route || config.route === "/" || config.routeType !== "api") {
    return `${file} requires the step's config.toml to set a route with route_type = "api".`;
  }

  return null;
}
